import { useRef, useEffect, useCallback } from 'react'

/**
 * Returns a debounced version of the provided async function.
 * The function will only execute after the specified delay has passed since the last call.
 * Designed to work with react-select's AsyncSelect callback-style API.
 * 
 * @param asyncFunction - The async function to debounce
 * @param delay - The delay in milliseconds
 * @returns A debounced function that accepts (inputValue, callback) parameters
 * 
 * @example
 * const fetchData = useCallback(async (input: string) => { ... }, [])
 * const debouncedFetch = useDebouncedCallback(fetchData, 300)
 * <AsyncSelect loadOptions={debouncedFetch} />
 */
export function useDebouncedCallback<T>(
    asyncFunction: (inputValue: string) => Promise<T[]>,
    delay: number
): (inputValue: string, callback: (options: T[]) => void) => void {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])

    return useCallback(
        (inputValue: string, callback: (options: T[]) => void) => {
            // Clear any existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }

            // Set new timeout
            timeoutRef.current = setTimeout(() => {
                asyncFunction(inputValue).then(callback).catch(() => callback([]))
            }, delay)
        },
        [asyncFunction, delay]
    )
}

